import React, { FunctionComponent, MutableRefObject, useEffect, useMemo, useRef } from 'react';
import urlJoin from 'url-join';
import useWidgetContext from '../../react/useWidgetContext';
import CONSTANTS from '../../constants/constants';
import { FC_SPC_CONFIG } from '../../constants/finCalcSpc';
import errorHandler from '../../service/errorHandler';
import { ViewType } from '../../enum/ViewModeType';
import { IFinCalcWidgetProps } from '../../model/IFinanceCalculatorWidget';
import { ICarDetails } from '../../../../../common/model/ICarDetails';
import { IFinCalcSpcInitConfig } from '../../react/useGetFinCalcSpcInitConfig';
import { assetManager } from '../../../../../common/service/assetManager';

interface IFinanceCalculatorWidget {
  carConfiguration: ICarDetails | IFinCalcSpcInitConfig;
  postInitialization?: (widgetInstance: any, widgetId: string) => void;
  additionalVariantIds: string[];
  postcode?: string;
}

const FinanceCalculatorWidget: FunctionComponent<IFinanceCalculatorWidget> = ({
  carConfiguration,
  postInitialization,
  additionalVariantIds,
  postcode,
}) => {
  const widgetId = useMemo(() => 'fc_widget' + Math.floor(Math.random() * 100), []);
  const widgetInstance: MutableRefObject<IFinCalcWidgetProps> = useRef(null);
  const finCalcSpcLibrary: any = useRef(null);
  const {
    configuration: { dimensions },
  } = useWidgetContext();

  const ensureLibraryLoaded = () => {
    const chunkUrl: string = urlJoin(FC_SPC_CONFIG.widgetUrl + FC_SPC_CONFIG.paths.chunkPath);
    const widgetUrl: string = urlJoin(FC_SPC_CONFIG.widgetUrl + FC_SPC_CONFIG.paths.widgetPath);

    if (assetManager.isScriptLoaded(chunkUrl) && assetManager.isScriptLoaded(widgetUrl)) {
      if (!finCalcSpcLibrary.current && (window as any).MiniSpc) {
        finCalcSpcLibrary.current = (window as any).MiniSpc;
      }

      return Promise.resolve();
    }

    return Promise.resolve()
      .then(() => {
        assetManager.loadScript(CONSTANTS.REACT_DEPENDENCIES.react);
        assetManager.loadScript(CONSTANTS.REACT_DEPENDENCIES.reactDom);
      })
      .then(() => assetManager.loadScript(chunkUrl))
      .then(() => assetManager.loadScript(widgetUrl))
      .then(() => {
        finCalcSpcLibrary.current = (window as any).MiniSpc;
      })
      .catch(e => {
        errorHandler.handleError(e);
      });
  };

  const initializeWidget = () => {
    if (widgetInstance.current) {
      widgetInstance.current.destroy();
    }

    const calculatorLibrary = (window as any).FC.InchcapeFinance;

    const config = {
      containerId: widgetId,
      vehicle: carConfiguration,
      viewMode: ViewType.FULL,
      additionalVariantIds,
      postcode,
      dimensions,
    };

    calculatorLibrary
      .createWidget(config)
      .then((widgetInstance: any) => {
        if (postInitialization) {
          postInitialization(widgetInstance, widgetId);
        }
      })
      .catch((e: any) => {
        errorHandler.handleError(e);
      });
  };

  useEffect(() => {
    if (!widgetInstance.current) {
      ensureLibraryLoaded()
        .then(() => initializeWidget())
        .catch(errorHandler.promise());
    }

    return () => {
      if (widgetInstance.current) {
        widgetInstance.current.destroy();
      }
    };
  }, []);

  return <div id={widgetId} data-test="common:finCalc-widget" />;
};

export default FinanceCalculatorWidget;
